<div class="dialog-wrapper">
  <div class="sticky-header">
    <div class="dialog-header">
      <div class="dialog-header__copy">
        <h2>Find a Pharmacy</h2>
        <p>Search by name or location, then choose where to send the refill.</p>
      </div>
      <app-icon-button icon="close" ariaLabel="Close" (click)="cancel()"></app-icon-button>
    </div>

    <mat-divider></mat-divider>

    <form [formGroup]="form" (submit)="search()">
      <div class="inputs-wrapper">
        <div class="search-tips">
          <p>Enter at least one field.</p>
          @if (showSavedZipHint()) {
            <span class="zip-pill">Using saved ZIP {{ patientZip() }}</span>
          }
        </div>

        <div class="field-grid">
          <mat-form-field id="name" appearance="outline" subscriptSizing="dynamic" floatLabel="always">
            <mat-label>Pharmacy Name</mat-label>
            <input matInput id="nameInput" formControlName="name" />
            @if (form.controls['name'].hasError('minlength')) {
              <mat-error id="errNameLength" data-test="matErrNameLength"
                >Enter at least 3 characters to search.</mat-error
              >
            }
          </mat-form-field>

          <mat-form-field id="city" appearance="outline" subscriptSizing="dynamic" floatLabel="always">
            <mat-label>City</mat-label>
            <input matInput id="cityInput" formControlName="city" />
            @if (form.controls['city'].hasError('minlength')) {
              <mat-error id="errCityLength" data-test="matErrCityLength"
                >Enter at least 3 characters to search.</mat-error
              >
            }
          </mat-form-field>

          <div class="field-grid__split">
            <mat-form-field id="state" appearance="outline" subscriptSizing="dynamic" floatLabel="always">
              <mat-label>State</mat-label>
              <input
                matInput
                id="stateInput"
                formControlName="state"
                mask="SS"
                pattern="^[A-Za-z]{2}$"
                [validation]="false"
                [dropSpecialCharacters]="false" />
              @if (form.controls['state'].hasError('pattern')) {
                <mat-error id="errStateAbbreviation">Use 2-letter state code (e.g., OH).</mat-error>
              }
            </mat-form-field>

            <mat-form-field id="zip" appearance="outline" subscriptSizing="dynamic" floatLabel="always">
              <mat-label>ZIP Code</mat-label>
              <input
                matInput
                id="zipInput"
                formControlName="zip"
                pattern="^[0-9]{5}$"
                mask="00000"
                [validation]="false"
                [dropSpecialCharacters]="false" />
              @if (form.controls['zip'].hasError('pattern')) {
                <mat-error id="errPostalCodeLength" data-test="matErrPostalCodeLength"
                  >Enter 5-digit ZIP code.</mat-error
                >
              }
            </mat-form-field>
          </div>
        </div>

        @if (form.hasError('noValues') && form.touched) {
          <div class="form-error">Enter at least one search field.</div>
        }

        <div class="search-actions">
          <button id="btnSearch" mat-flat-button type="submit" class="search-button" color="primary">Search</button>
        </div>
      </div>
    </form>
  </div>

  <div
    class="search-results"
    id="scroll"
    infinite-scroll
    [scrollWindow]="false"
    [infiniteScrollDistance]="2"
    [infiniteScrollThrottle]="1000"
    (scrolled)="onScroll()">
    @if (pharmacies().length > 0) {
      <div class="results-header" aria-hidden="true">
        <span>Pharmacy</span>
        <span>Address</span>
        <span>Phone</span>
        <span class="results-header__action">
          <app-icon name="open_in_new" size="xs" color="on-surface-variant"></app-icon>
        </span>
      </div>
      <div class="pharmacy-results">
        @for (pharmacy of pharmacies(); track pharmacy.pharmacyId) {
          <div
            class="pharmacy-container cursor"
            role="button"
            tabindex="0"
            (click)="pharmacyClick(pharmacy)"
            (keydown.enter)="pharmacyClick(pharmacy)">
            <div class="pharmacy-name pharmacy-cell">
              <p>{{ pharmacy.name }}</p>
            </div>
            <div class="pharmacy-address pharmacy-cell">
              @if (pharmacy.addressLine1 !== null) {
                <div class="pharmacy-line-item">
                  <div class="pharmacy-address__content">
                    <p class="address-line-1" [attr.title]="pharmacy.addressLine1">{{ pharmacy.addressLine1 }}</p>
                    @if (pharmacy.addressLine2) {
                      <p>{{ pharmacy.addressLine2 }}</p>
                    }
                    <p>{{ pharmacy.city }}, {{ pharmacy.state }} {{ pharmacy.postalCode }}</p>
                  </div>
                </div>
              } @else {
                <p>No address on file</p>
              }
            </div>
            <div class="pharmacy-phone pharmacy-cell">
              @if (pharmacy.primaryTelephone !== null) {
                <div class="pharmacy-line-item">
                  <a
                    class="custom-widget-link"
                    [href]="'tel:' + pharmacy.primaryTelephone"
                    (click)="$event.stopPropagation()">
                    {{ pharmacy.primaryTelephone | mask: '(000) 000-0000' }}
                  </a>
                </div>
              } @else {
                <p>No phone on file</p>
              }
            </div>
            <div class="pharmacy-actions pharmacy-cell">
              @if (pharmacy.addressLine1 !== null) {
                <button
                  (click)="getMapsUrl($event, pharmacy, isSafari())"
                  class="custom-widget-link directions-link"
                  id="addressLink"
                  aria-label="Open directions"
                  type="button">
                  <app-icon name="open_in_new" size="xs" color="on-surface"></app-icon>
                </button>
              }
            </div>
          </div>
        }
      </div>
      @if (isLoading()) {
        <div class="loading-more" aria-live="polite" aria-label="Loading more pharmacies">
          <app-progress-spinner [diameter]="28"></app-progress-spinner>
        </div>
      }
    } @else if (isLoading()) {
      <app-progress-spinner data-test="spinnerMedications" class="loading"></app-progress-spinner>
    } @else if (hasSearched) {
      <app-empty-state
        iconName="local_pharmacy"
        title="No pharmacies found"
        subtitle="Try changing your pharmacy name or location filters."
        hint="Refine your search criteria and try again.">
      </app-empty-state>
    } @else {
      <app-empty-state
        iconName="search"
        title="Search for a pharmacy"
        subtitle="Enter a pharmacy name or location to see matching results."
        hint="You can search by pharmacy name, city, state, or ZIP code.">
      </app-empty-state>
    }
  </div>
</div>
